import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import type { IResourceItemType } from '@/api/types';
import { ViewAction, ICommonItemColumnNames } from '@/components/common';
import type { ITableSortingState } from '@/components/common/table/types';

import {
  getDescription,
  getLabel,
  isResourceAnnouncementItem,
  isResourceDocumentItem,
  isResourceLinkItem,
} from '@/helpers';
import { VisibilityChip } from '.';
import {
  Table,
  DropdownMenu,
  ResourcesIcon,
  ResourceTableCell,
  ResourceTableHeader,
  ResourceTableHeaderCell,
  ResourceTableRow,
} from '@/components/common';
import { useTimezone } from '@/hooks';
import { MONTH_DAY_YEAR_HOUR_FORMAT_NBSP } from '@/config/variables';
import { otherColors } from '@/styles/colors';

interface IProps {
  items: IResourceItemType[];
  sortingState: ITableSortingState<ICommonItemColumnNames>;
  onSort: (columnName: ICommonItemColumnNames) => void;
  openEditModal: (item: IResourceItemType) => void;
  openDeleteModal: (item: IResourceItemType) => void;
}

const ResourceItemsTable = ({
  items,
  sortingState,
  onSort,
  openEditModal,
  openDeleteModal,
}: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();

  return (
    <Table>
      <ResourceTableHeader>
        <ResourceTableHeaderCell width="26px" minWidth="26px" />
        <ResourceTableHeaderCell
          size={3}
          sortingEnabled
          sortingState={sortingState.name}
          onClick={() => onSort('name')}
        >{t`resources.foldersList.headerName`}</ResourceTableHeaderCell>
        {items[0] && !isResourceAnnouncementItem(items[0]) && (
          <ResourceTableHeaderCell size={3}>{t`resources.foldersList.headerDescription`}</ResourceTableHeaderCell>
        )}
        <ResourceTableHeaderCell
          size={2}
          sortingEnabled
          sortingState={sortingState.visibility}
          onClick={() => onSort('visibility')}
        >{t`resources.foldersList.headerVisibility`}</ResourceTableHeaderCell>
        <ResourceTableHeaderCell
          size={2}
          sortingEnabled
          sortingState={sortingState.dateStart}
          onClick={() => onSort('dateStart')}
        >{t`resources.foldersList.headerPostedDate`}</ResourceTableHeaderCell>
        <ResourceTableHeaderCell
          size={2}
          sortingEnabled
          sortingState={sortingState.dateEnd}
          onClick={() => onSort('dateEnd')}
        >{t`resources.foldersList.headerExpiration`}</ResourceTableHeaderCell>
        <ResourceTableHeaderCell
          size={2}
          sortingEnabled
          sortingState={sortingState.createdByName}
          onClick={() => onSort('createdByName')}
        >{t`resources.foldersList.headerCreatedBy`}</ResourceTableHeaderCell>
        <ResourceTableHeaderCell
          size={2}
          sortingEnabled
          sortingState={sortingState.modifiedByName}
          onClick={() => onSort('modifiedByName')}
        >{t`resources.foldersList.headerModifiedBy`}</ResourceTableHeaderCell>
        {items.length && isResourceDocumentItem(items[0]) && <ResourceTableHeaderCell width="34px" minWidth="34px" />}
        <ResourceTableHeaderCell width="26px" minWidth="26px" className="p-0 ms-3" />
      </ResourceTableHeader>
      {items.map((item, id) => (
        <ResourceTableRow key={id}>
          <IconCell width="26px" minWidth="26px" className="m-0">
            <ResourcesIcon data={item} />
          </IconCell>
          <ResourceTableCell multiline={2} size={3}>
            {isResourceLinkItem(item) ? (
              <LinkName href={item.url} target="_blank">
                {getLabel(item)}
              </LinkName>
            ) : (
              getLabel(item)
            )}
          </ResourceTableCell>
          {!isResourceAnnouncementItem(item) && (
            <ResourceTableCell multiline={2} size={3}>
              {getDescription(item)}
            </ResourceTableCell>
          )}
          <ResourceTableCell size={2}>
            <VisibilityChip visibility={item.publicationStatus} />
          </ResourceTableCell>
          <ResourceTableCell multiline={2} size={2}>
            {item.dateStart && formatDateTime(item.dateStart, MONTH_DAY_YEAR_HOUR_FORMAT_NBSP)}
          </ResourceTableCell>
          <ResourceTableCell multiline={2} size={2}>
            {item.dateEnd && formatDateTime(item.dateEnd, MONTH_DAY_YEAR_HOUR_FORMAT_NBSP)}
          </ResourceTableCell>
          <ResourceTableCell multiline={2} size={2}>
            {item.createdByName?.replace('.', ' ')}
          </ResourceTableCell>
          <ResourceTableCell multiline={2} size={2}>
            {item.modifiedByName?.replace('.', ' ')}
          </ResourceTableCell>
          {isResourceDocumentItem(item) && (
            <ResourceTableCell width="16px" minWidth="16px">
              <ViewAction href={item.path} />
            </ResourceTableCell>
          )}
          <ResourceTableCell width="24px" minWidth="24px" className="dropdown-action">
            <DropdownMenu
              options={[
                {
                  label: t`common.btnEdit`,
                  callback: () => openEditModal(item),
                },
                {
                  label: t`common.btnDelete`,
                  callback: () => openDeleteModal(item),
                },
              ]}
            />
          </ResourceTableCell>
        </ResourceTableRow>
      ))}
    </Table>
  );
};

export { ResourceItemsTable };

const LinkName = styled.a`
  color: ${otherColors.lxc.blue};
`;

const IconCell = styled(ResourceTableCell)`
  align-self: flex-start;
`;
